import React, { useEffect, useState } from "react";
import { RightOutlined, CloseOutlined } from "@ant-design/icons";
import {
  MonitorSmartphone,
  Thermometer,
  Settings2,
  UserCheck,
  ThermometerSnowflake,
} from "lucide-react";
import AnimatedSection from "@/components/animation/AnimatedSection/AnimatedSection";
import WhatsAppFloatingButton from "@/components/WhatsAppFloatingButton";
import { Link, useParams, Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";
import OptImg from "@/components/OptImg";

/* ==== Icons registry (unchanged) ==== */
const ICON_MAP = {
  MonitorSmartphone,
  Thermometer,
  Settings2,
  UserCheck,
  ThermometerSnowflake,
};

const ARCTIC_STORE_COLUMNS = [
  { group: "Dimensions", label: "Dimensions", key: "size" },
  { group: "External", label: "Length (m)", key: "external_length" },
  { group: "External", label: "Width (m)", key: "external_width" },
  { group: "External", label: "Height (m)", key: "external_height" },
  { group: "Internal", label: "Length (m)", key: "internal_length" },
  { group: "Internal", label: "Width (m)", key: "internal_width" },
  { group: "Internal", label: "Height (m)", key: "internal_height" },
  { group: "Internal", label: "Cubic Capacity (m3)", key: "cubic_capacity" },
  { group: "Detail Capacity", label: "Max Gross (kg)", key: "max_gross" },
  { group: "Detail Capacity", label: "Tare (kg)", key: "tare" },
  { group: "Detail Capacity", label: "Net (kg)", key: "net" },
];

const ARCTIC_STORE_DIMENSIONS = [
  {
    size: "10 FT",
    external_length: "3.000",
    external_width: "2.450",
    external_height: "2.620",
    internal_length: "2.360",
    internal_width: "2.292",
    internal_height: "2.230",
    cubic_capacity: "12.50",
    max_gross: "10.160",
    tare: "2.060",
    net: "8.100",
  },
  {
    size: "20 FT",
    external_length: "6.058",
    external_width: "2.450",
    external_height: "2.620",
    internal_length: "5.427",
    internal_width: "2.292",
    internal_height: "2.230",
    cubic_capacity: "28.80",
    max_gross: "24.000",
    tare: "2.770",
    net: "21.230",
  },
  {
    size: "40 HC",
    external_length: "12.193",
    external_width: "2.450",
    external_height: "2.900",
    internal_length: "11.560",
    internal_width: "2.292",
    internal_height: "2.530",
    cubic_capacity: "69.90",
    max_gross: "30.480",
    tare: "4.650",
    net: "25.830",
  },
];

const ULTRA_FREEZER_DIMENSIONS = [
  {
    size: "10 FT",
    external_length: "2.99",
    external_width: "2.44",
    external_height: "2.59",
    internal_length: "2.36",
    internal_width: "2.08",
    internal_height: "2.31",
    cubic_capacity: "12.1",
    max_gross: "10,160",
    tare: "1,700",
    net: "8,460",
  },
  {
    size: "20 FT",
    external_length: "6.06",
    external_width: "2.44",
    external_height: "2.59",
    internal_length: "5.46",
    internal_width: "2.29",
    internal_height: "2.31",
    cubic_capacity: "28.3",
    max_gross: "24,000",
    tare: "2,660",
    net: "21,340",
  },
  {
    size: "40 FT HC",
    external_length: "12.19",
    external_width: "2.44",
    external_height: "2.89",
    internal_length: "11.55",
    internal_width: "2.29",
    internal_height: "2.60",
    cubic_capacity: "68.6",
    max_gross: "34,000",
    tare: "4,410",
    net: "29,590",
  },
];

const ARTIC_BLAST_DIMENSIONS = [
  {
    size: "10 FT",
    external_length: "3.000",
    external_width: "2.450",
    external_height: "2.620",
    internal_length: "2.360",
    internal_width: "2.292",
    internal_height: "2.230",
    cubic_capacity: "12.50",
    max_gross: "10.160",
    tare: "2.060",
    net: "8.100",
  },
  {
    size: "20 FT",
    external_length: "6.058",
    external_width: "2.450",
    external_height: "2.620",
    internal_length: "5.427",
    internal_width: "2.292",
    internal_height: "2.230",
    cubic_capacity: "28.80",
    max_gross: "24.000",
    tare: "2.770",
    net: "21.230",
  },
  {
    size: "40 HC",
    external_length: "12.193",
    external_width: "2.450",
    external_height: "2.900",
    internal_length: "11.560",
    internal_width: "2.292",
    internal_height: "2.530",
    cubic_capacity: "69.90",
    max_gross: "30.480",
    tare: "4.650",
    net: "25.830",
  },
];

// === Products Array ===
const PRODUCTS = [
  {
    slug: "arcticstore",
    img: "https://res.cloudinary.com/dmvui9sur/image/upload/v1753458902/ArcticStore_Container_without_bg_gwre0q.png",
    dimensions: {
      columns: ARCTIC_STORE_COLUMNS,
      data: ARCTIC_STORE_DIMENSIONS,
    },
    features: [
      { icon: "MonitorSmartphone" },
      { icon: "Thermometer" },
      { icon: "Settings2" },
      { icon: "Settings2" },
      { icon: "UserCheck" },
      { icon: "MonitorSmartphone" },
    ],
    variants: [
      {
        img: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754121153/AS-10FT.w_xo2fj5.png",
      },
      {
        img: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754121148/AS-20Ft.w_dp5xog.png",
      },
      {
        img: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754121141/AS-40HQ.w_ckboxy.png",
      },
    ],
  },
  {
    slug: "ultrafreezer",
    img: "https://res.cloudinary.com/dmvui9sur/image/upload/v1753458896/Arcticstore_Ultrafreezer_without_bg_kcwxaq.png",
    dimensions: {
      columns: ARCTIC_STORE_COLUMNS,
      data: ULTRA_FREEZER_DIMENSIONS,
    },
    variants: [
      {
        img: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754217150/UF-20FT.ww__mbgude.png",
      },
      {
        img: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754217105/UF-40HQ.w_bymqli.png",
      },
    ],
  },
  {
    slug: "arcticblast",
    img: "https://res.cloudinary.com/dmvui9sur/image/upload/v1753458926/Foto_ABF_40_v12pb9.png",
    dimensions: { columns: ARCTIC_STORE_COLUMNS, data: ARTIC_BLAST_DIMENSIONS },
    variants: [
      {
        img: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754217436/arcticblast-floating_yyjdnn.png",
      },
      {
        img: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754217504/ABF-40HQ.w_p8yxh5.png",
      },
    ],
  },
  {
    slug: "arcticsuperstore",
    img: "https://res.cloudinary.com/dmvui9sur/image/upload/v1753458907/Arctic_SuperStore_yaacsr.png",
    variants: [
      {
        img: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754217676/SS-5-Bay.w_p3i8gn.png",
      },
      {
        img: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754217734/SS-3-Bay_yevmuh.png",
      },
      {
        img: "https://res.cloudinary.com/dmvui9sur/image/upload/v1754217742/SS-2-Bay.w_ozugvg.png",
      },

    ],
  },
];

const ArcticStoreDimensionsSection = ({ data, columns }) => {
  const { t } = useTranslation("productDetail");

  // localize group headings/labels
  const localizedCols = columns.map((c) => ({
    ...c,
    group: t(`table.groups.${c.group}`),
    label: t(`table.columns.${c.label}`),
  }));
  const groupColspan = localizedCols.reduce((acc, col) => {
    acc[col.group] = (acc[col.group] || 0) + 1;
    return acc;
  }, {});

  return (
    <section className="w-full mt-8 mb-8">
      <div className="w-full max-w-[90vw] overflow-x-auto rounded-xl shadow border border-gray-200">
        <table className="min-w-[800px] w-full bg-white text-black text-base">
          <thead>
            <tr>
              {Object.entries(groupColspan).map(([group, span], idx) => (
                <th
                  key={`${group}-${idx}`}
                  colSpan={span}
                  className={`bg-gray-700 text-white font-semibold py-3 px-2 text-left border-b border-gray-200 ${
                    idx === 0 ? "rounded-tl-xl" : ""
                  }`}
                >
                  {group}
                </th>
              ))}
            </tr>
            <tr>
              {localizedCols.map((col, idx) => (
                <th
                  key={col.key}
                  className={`bg-gray-600 text-white font-medium py-2 px-2 text-left border-b border-gray-200 ${
                    idx === localizedCols.length - 1 ? "rounded-tr-xl" : ""
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr
                key={row.size || i}
                className={i % 2 === 1 ? "bg-gray-50" : ""}
              >
                {localizedCols.map((col) => (
                  <td
                    key={col.key}
                    className="py-2 px-2 border-b border-gray-100"
                  >
                    {row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

/* ==== Interested CTA (localized) ==== */
const InterestedSection = () => {
  const { t, i18n } = useTranslation("productDetail");
  const lang = i18n.language === "id" ? "id" : "en";
  
  return (
    <section className="w-full bg-gradient-to-tr from-[#e0ecff] to-[#f8fafc] py-10 px-4 sm:px-0">
      <div className="max-w-2xl mx-auto flex flex-col items-center gap-4 text-center">
        <div className="text-2xl sm:text-3xl font-bold text-black mb-2">
          {t("interested.title")}
        </div>
        <div className="text-base sm:text-lg text-gray-700 mb-6">
          {t("interested.desc")}
        </div>
        <Link
          to={`/${lang}/get-started`}
          className="inline-flex items-center px-6 py-3 rounded-full bg-black text-white font-semibold text-lg shadow-md hover:bg-gray-800 focus:bg-gray-800 transition"
        >
          {t("interested.cta")}
        </Link>
      </div>
    </section>
  );
};

/* ==== Other products (localized heading) ==== */
const OtherProductSection = ({ currentSlug }) => {
  const { t, i18n } = useTranslation("productDetail");
  const lang = i18n.language === "id" ? "id" : "en";
  const recommendations = PRODUCTS.filter((p) => p.slug !== currentSlug).slice(
    0,
    3
  );

  return (
    <section className="w-full bg-gray-50 py-10 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        <h3 className="text-2xl font-bold text-black mb-8 text-center">
          {t("otherProducts.title")}
        </h3>
        <div className="grid gap-12 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 place-items-center">
          {recommendations.map((prod) => (
            <div key={prod.slug} className="flex flex-col items-center group">
              <OptImg
                src={prod.img}
                alt={t(`products.${prod.slug}.title`)}
                className="h-40 object-contain drop-shadow-xl transition-transform duration-300 group-hover:-translate-y-2 group-hover:scale-105 select-none"
                draggable={false}
                style={{ maxWidth: "90%" }}
              />
              <div className="font-semibold text-lg text-black mb-3 mt-4 text-center">
                {t(`products.${prod.slug}.title`)}
              </div>
              <Link
                to={`/${lang}/product/${prod.slug}`}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-black text-white hover:bg-gray-800 focus:bg-gray-800 transition shadow-md group-hover:scale-110 outline-none mb-2"
                aria-label={t("otherProducts.seeDetails", {
                  title: t(`products.${prod.slug}.title`),
                })}
              >
                <RightOutlined style={{ fontSize: 18 }} />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ==== Product main section (localized strings from slug) ==== */
const ProductDetailSection = ({ product }) => {
  const { t } = useTranslation("productDetail");
  const slug = product.slug;

  return (
    <section className="w-full bg-white px-6 sm:px-12 py-12 mb-8">
      <div className="max-w-7xl mx-auto flex flex-col gap-8 items-center">
        <div className="w-full flex flex-col items-center text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-black mb-4">
            {t(`products.${slug}.title`)}
          </h1>
          <div className="w-full flex justify-center">
            <OptImg
              src={product.img}
              priority
              alt={t(`products.${slug}.title`)}
              className="w-full max-w-[90vw] md:max-w-[600px] h-auto object-contain mx-auto"
            />
          </div>
          <h2 className="text-xl sm:text-2xl font-semibold text-[#555] mb-4 text-left">
            {t(`products.${slug}.subtitle`)}
          </h2>
          <p
            className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6 text-left"
            style={{ whiteSpace: "pre-line" }}
          >
            {t(`products.${slug}.desc`)}
          </p>

          {/* Dimensions */}
          {product.dimensions && (
            <>
              <h2 className="text-4xl sm:text-5xl font-bold text-black mb-4 mt-8">
                {t("size.title")}
              </h2>
              <ArcticStoreDimensionsSection
                data={product.dimensions.data}
                columns={product.dimensions.columns}
              />
            </>
          )}
        </div>
      </div>
    </section>
  );
};

/* ==== Features (labels by index under the slug) ==== */
const FeaturesSection = ({ features, slug }) => {
  const { t } = useTranslation("productDetail");

  return (
    <section className="w-full bg-black py-14 px-4">
      <div className="max-w-6xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-10 tracking-tight">
          {t("features.title")}
        </h2>

        {/* Desktop */}
        <div className="hidden md:grid grid-cols-3 grid-rows-2 gap-8 place-items-center w-full">
          {features.map((f, idx) => {
            const Icon = ICON_MAP[f.icon] || MonitorSmartphone;
            return (
              <div
                key={idx}
                className="w-full flex flex-col items-center justify-center aspect-square bg-white/10 rounded-[2.5rem] border border-white/30 shadow-[0_4px_48px_rgba(0,0,0,0.14)] backdrop-blur-[2px] p-8 transition-all hover:bg-white/20 max-w-[290px]"
              >
                <span className="mb-6 flex items-center justify-center w-16 h-16 rounded-full border border-white/30">
                  <Icon size={48} strokeWidth={1.4} className="text-white" />
                </span>
                <div className="text-center text-white text-lg font-medium mt-2 leading-snug">
                  {t(`products.${slug}.features.${idx}`)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile */}
        <div
          className="md:hidden flex justify-center overflow-x-auto scrollbar-none pb-2 w-full"
          style={{
            WebkitOverflowScrolling: "touch",
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          <div
            className="grid grid-rows-3 grid-flow-col gap-4 justify-items-center items-center min-w-[340px] max-w-full mx-auto px-1"
            style={{ width: `${Math.ceil(features.length / 3) * 180}px` }}
          >
            {features.map((f, idx) => {
              const Icon = ICON_MAP[f.icon] || MonitorSmartphone;
              return (
                <div
                  key={idx}
                  className="flex flex-col items-center justify-center w-[150px] h-[140px] bg-white/10 rounded-[2.5rem] border border-white/30 shadow-[0_4px_48px_rgba(0,0,0,0.14)] backdrop-blur-[2px] p-5 transition-all hover:bg-white/20 mx-auto"
                >
                  <span className="mb-4 flex items-center justify-center w-14 h-14 rounded-full border border-white/30">
                    <Icon size={38} strokeWidth={1.4} className="text-white" />
                  </span>
                  <div className="text-center text-white text-base font-medium mt-2 leading-snug">
                    {t(`products.${slug}.features.${idx}`)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ==== Image modal (unchanged) ==== */
const ImageModal = ({ open, img, name, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 transition-all">
      <div className="relative bg-white rounded-2xl shadow-lg p-4 md:p-8 max-w-lg w-full">
        <button
          className="absolute top-2 right-2 text-gray-500 hover:text-black"
          onClick={onClose}
          aria-label="Close"
        >
          <CloseOutlined style={{ fontSize: 20 }} />
        </button>
        <OptImg
          src={img}
          alt={name}
          className="max-w-full max-h-[60vh] rounded-xl mx-auto object-contain"
        />
        <div className="text-xl font-semibold text-black text-center mt-4">
          {name}
        </div>
      </div>
    </div>
  );
};

/* ==== Variants (variant names localized by index) ==== */
const ProductVariantsSection = ({ variants, slug }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImg, setModalImg] = useState("");
  const [modalName, setModalName] = useState("");
  const { t } = useTranslation("productDetail");

  useEffect(() => {
    setModalOpen(false);
    setModalImg("");
    setModalName("");
  }, [variants, slug]);

  const openModal = (img, name) => {
    setModalImg(img);
    setModalName(name);
    setModalOpen(true);
  };

  return (
    <section className="w-full py-12 bg-gray-50 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-black mb-10 tracking-tight text-center">
          {t("variants.title")}
        </h2>

        <div className="flex flex-wrap justify-center gap-8 max-w-5xl mx-auto">
          {variants?.map((variant, i) => {
            const vName = t(`products.${slug}.variants.${i}.name`);
            return (
              <button
                key={vName}
                className="group flex flex-col items-center bg-transparent p-0 focus:outline-none w-full max-w-xs"
                onClick={() => openModal(variant.img, vName)}
                aria-label={t("variants.view", { name: vName })}
                type="button"
                tabIndex={0}
              >
                <div className="w-full flex items-center justify-center mb-4">
                  <OptImg
                    src={variant.img}
                    alt={vName}
                    className="max-h-40 object-contain drop-shadow-xl transition duration-300 group-hover:-translate-y-2 group-hover:scale-105"
                    loading="lazy"
                    draggable={false}
                    style={{ minHeight: 110, minWidth: 120 }}
                  />
                </div>
                <div className="mt-2 text-base font-semibold text-black group-hover:text-[#1746A2] text-center transition">
                  {vName}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <ImageModal
        open={modalOpen}
        img={modalImg}
        name={modalName}
        onClose={() => setModalOpen(false)}
      />
    </section>
  );
};

/* ==== Page ==== */
const ProductDetailPage = () => {
  const { productName } = useParams();
  const product = PRODUCTS.find(
    (p) => p.slug.toLowerCase() === productName.toLowerCase()
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [productName]);

  if (!product) return <Redirect to="/404" />;

  return (
    <>
      <AnimatedSection>
        <ProductDetailSection product={product} />
      </AnimatedSection>

      <AnimatedSection>
        {product.features && (
          <FeaturesSection features={product.features} slug={product.slug} />
        )}
      </AnimatedSection>

      <AnimatedSection>
        {product.variants && (
          <ProductVariantsSection
            variants={product.variants}
            slug={product.slug}
          />
        )}
      </AnimatedSection>

      <AnimatedSection>
        <InterestedSection />
      </AnimatedSection>

      <AnimatedSection>
        <OtherProductSection currentSlug={product.slug} />
      </AnimatedSection>

      {/* <WhatsAppFloatingButton /> */}
    </>
  );
};

export default ProductDetailPage;
